import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useTokenUsdPrice } from 'state/app/hooks'
import styled from 'styled-components'
import theme from 'theme'
import { FlexProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import { buildPaymentAmount } from 'views/BonusCenter/hooks'
import { emptyBonus } from 'views/Payment/components/Deposit/DepositCryptos/hooks'
import { ExtraInfoLabel } from 'views/Payment/components/ExtraInfo'
import { PaymentChevronRightIcon, PaymentWrapIcon } from './styles'
import WaitingDepositBonusModal from './WaitingDepositBonusModal'
import WaitingDepositBonusCountdown from './WaitingDepositBonusModal/WaitingDepositBonusCountdown'

const SelectedBonusItem: React.FC<
  {
    selectedToken: Token
    inputAmount: string | null

    bonusControl: {
      selectedBonus?: WageringConditionBonus
      bonuses?: WageringConditionBonus[]
      isSelectedBonusExpired?: boolean
      handleSelectedBonus?: (bonus: WageringConditionBonus) => void
    }
  } & FlexProps
> = ({ bonusControl, selectedToken, inputAmount }) => {
  const [onPresentBonus] = useModal(WaitingDepositBonusModal)
  const tokenPrice = useTokenUsdPrice(selectedToken)
  const { t } = useTranslation()

  const amount = new BigNumber(bonusControl?.selectedBonus?.minDepositBigAmount)
    .dividedBy(tokenPrice)
    .decimalPlaces(6, BigNumber.ROUND_CEIL)

  const { display } = useMemo(
    () =>
      buildPaymentAmount(
        {
          amount: inputAmount,
          tokenInUsd: tokenPrice?.toString(10) || 0,
        },
        selectedToken,
        t,
        bonusControl.selectedBonus,
      ),
    [inputAmount, tokenPrice, selectedToken, t, bonusControl.selectedBonus],
  )

  const isValid =
    inputAmount === null ||
    inputAmount === '' ||
    (Number(inputAmount) > 0 && bonusControl?.selectedBonus?.minDepositBigAmount && amount.lte(inputAmount))

  const isAlmostExpire = useMemo(() => {
    if (bonusControl?.selectedBonus?.condition?.isExpired) return false
    const expiryDate = (bonusControl?.selectedBonus?.condition?.expiryDate || 0) - Date.now()
    return expiryDate > 0 && expiryDate < 30 * 60 * 1000
  }, [bonusControl?.selectedBonus?.condition?.expiryDate])

  return (
    <Column gap="var(--pm-space-label, 8px)">
      <RowBetween>
        <Text color="white" small lineHeight={1.4}>
          <Trans>Bonus</Trans>
        </Text>

        <StyledWrapItem
          height="21px"
          gap="8px"
          onClick={() =>
            onPresentBonus({
              bonusControl,
              selectedToken,
            })
          }
        >
          {bonusControl.selectedBonus?.id !== emptyBonus.id && selectedToken && display ? (
            <StyleSelectBonus style={{ color: isValid ? theme.colors.backgroundAlt13 : theme.colors.textAlt699 }}>
              {display}
            </StyleSelectBonus>
          ) : (
            <Text fontSize="12px" small color="currentColor">
              <Trans>Choose Bonus</Trans>
            </Text>
          )}
          <RowMiddle width={12}>
            <PaymentChevronRightIcon />
          </RowMiddle>
        </StyledWrapItem>
      </RowBetween>

      {bonusControl?.selectedBonus?.minDepositBigAmount && selectedToken && (
        <Text color="var(--pm-text-note-color)" fontSize="var(--pm-text-note-size)">
          {t('Deposit at least {{amount}} {{unit}} to active this bonus.', {
            amount: amount.toString(),
            unit: selectedToken.code,
          })}
        </Text>
      )}
      {isAlmostExpire && (
        <RowBetween>
          <ExtraInfoLabel>
            <Trans>Your bonus will expired in</Trans>
          </ExtraInfoLabel>
          <WaitingDepositBonusCountdown
            color={'white'}
            expiryDate={bonusControl?.selectedBonus.condition?.expiryDate}
            onBonusExpired={() => {
              if (bonusControl?.handleSelectedBonus) {
                bonusControl?.handleSelectedBonus(emptyBonus)
              }
            }}
          />
        </RowBetween>
      )}
    </Column>
  )
}

const StyledWrapItem = styled(PaymentWrapIcon)`
  min-height: 22px;
`

const StyleSelectBonus = styled(Text)`
  padding: 2px 6px;
  color: currentColor;
  border: 1px solid currentColor;
  font-size: var(--pm-text-note-size);
  border-radius: 4px;
  cursor: pointer;
`

export default SelectedBonusItem
